import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildClassLabelIndex, resolveClassIdentity, type ClassLabelIndexRow } from "@/lib/classLabels";

/**
 * Executive-level insights for the Registrations page.
 *
 * READ ONLY. This hook reads the SAME sources RegistrationsPanel uses
 * (get-stripe-payments edge function + class_interest table + admin_read_markers)
 * so counts match the panel below. We never fork the data model here.
 *
 * Interest-form resolution goes through the canonical resolver in
 * @/lib/classLabels — the same one used by RegistrationsPanel, MAVIS, and
 * the verifier tests. Historical rows without a schedule_option/weekday are
 * VALID "General Interest" records, never "needs attention".
 */

export interface RegPayment {
  id: string;
  email: string;
  amount: number;
  status: string; // "paid" | "pending" | "failed" | "processing" | ...
  created: number; // unix seconds
  class_id: string;
  adult_name: string;
  child_name: string;
  selected_sessions: string;
}

export interface RegInterest {
  id: string;
  class_id: string;
  adult_name: string;
  email: string;
  child_name: string;
  child_age: number;
  created_at: string;
  schedule_option: string | null;
  weekday: string | null;
  enrollment_period: string | null;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;


export function useRegistrationsInsights() {
  const [payments, setPayments] = useState<RegPayment[]>([]);
  const [interest, setInterest] = useState<RegInterest[]>([]);
  const [readMarkers, setReadMarkers] = useState<Set<string>>(new Set());
  const [classRows, setClassRows] = useState<ClassLabelIndexRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const [pRes, iRes, mRes, cRes] = await Promise.all([
      supabase.functions.invoke("get-stripe-payments"),
      supabase
        .from("class_interest")
        .select("id, class_id, adult_name, email, child_name, child_age, created_at, schedule_option, weekday, enrollment_period")
        .order("created_at", { ascending: false }),
      supabase.from("admin_read_markers").select("item_type,item_id"),
      (supabase as any).from("classes").select("id, slug, title"),
    ]);
    const all: RegPayment[] = (pRes.data as any)?.payments ?? [];
    // Match panel: hide records older than the 2026-03-20 cutoff (legacy noise).
    const cutoff = new Date("2026-03-20T00:00:00Z").getTime() / 1000;
    setPayments(all.filter((p) => p.created >= cutoff));
    setInterest((iRes.data as any) ?? []);
    const markers = new Set<string>();
    for (const row of (mRes.data as any) ?? []) {
      markers.add(`${row.item_type}:${row.item_id}`);
    }
    setReadMarkers(markers);
    setClassRows(((cRes.data as any) ?? []) as ClassLabelIndexRow[]);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const classIndex = useMemo(() => buildClassLabelIndex(classRows), [classRows]);
  const resolveTitle = (id: string) => resolveClassIdentity(id, classIndex).title;
  const isUnresolved = (id: string) => resolveClassIdentity(id, classIndex).isUnknown;

  const insights = useMemo(() => {
    const now = Date.now();
    const weekAgo = now - WEEK_MS;

    const newPayments = payments.filter((p) => p.created * 1000 >= weekAgo);
    const newInterest = interest.filter(
      (i) => new Date(i.created_at).getTime() >= weekAgo,
    );

    const isPaid = (s: string) => s === "paid" || s === "complete";
    const isProblem = (s: string) => s === "failed" || s === "requires_payment_method";
    const isPending = (s: string) => s === "pending" || s === "processing" || s === "awaiting";

    const failedPayments = payments.filter((p) => isProblem(p.status));
    const pendingPayments = payments.filter((p) => isPending(p.status));

    const revenueThisMonth = payments
      .filter((p) => {
        const d = new Date(p.created * 1000);
        return (
          isPaid(p.status) &&
          d.getFullYear() === new Date().getFullYear() &&
          d.getMonth() === new Date().getMonth()
        );
      })
      .reduce((s, p) => s + (p.amount ?? 0), 0);

    // Only truly unresolved interest rows (class itself can't be identified)
    // count as "needs attention". Rows without a schedule_option/weekday are
    // valid General Interest submissions — those pre-date the day picker and
    // are complete records, not missing data.
    const unresolvedInterest = interest.filter((i) => isUnresolved(i.class_id));

    const unreadPayments = payments.filter(
      (p) => !readMarkers.has(`payment:${p.id}`),
    );
    const unreadInterest = interest.filter(
      (i) => !readMarkers.has(`interest:${i.id}`),
    );

    // Top classes by new activity this week (helps the Focus card explain WHY).
    const classActivity = new Map<string, number>();
    for (const p of newPayments) classActivity.set(p.class_id, (classActivity.get(p.class_id) ?? 0) + 1);
    for (const i of newInterest) classActivity.set(i.class_id, (classActivity.get(i.class_id) ?? 0) + 1);
    const topClasses = [...classActivity.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id, n]) => ({ classId: id, title: resolveTitle(id), count: n }));

    return {
      totals: {
        paidRegistrations: payments.filter((p) => isPaid(p.status)).length,
        interestForms: interest.length,
        revenueThisMonth,
      },
      newThisWeek: {
        payments: newPayments.length,
        interest: newInterest.length,
      },
      needsAttention: {
        failed: failedPayments.length,
        pending: pendingPayments.length,
        unresolvedInterest: unresolvedInterest.length,
        unreadPayments: unreadPayments.length,
        unreadInterest: unreadInterest.length,
      },
      records: {
        newPayments,
        newInterest,
        failedPayments,
        pendingPayments,
        unresolvedInterest,
        unreadPayments,
        unreadInterest,
      },
      topClasses,
      resolveTitle,
    };
  }, [payments, interest, readMarkers, classIndex]);

  return { loading, insights, refresh };
}

